import {gmAPI} from '../MapService';
import {CIRCLE_SVG, PIVOT_SVG, SQUARE_SVG, computeOffset, getRotatorCursor, getScaleCursor, Edge, getEdges} from './editUtilities';
import {Vector} from '@/lib/classes/Vector';

// Types
import {ImageData} from './ImageGraphic/ImageGraphic';

/**
 * This class heavily borrows from Andrew's image upload/kmz generation tool.
 * Link to the tool: http://maps.andrewgriesemer.com/overlay/index.html
 * If you want to compare the source code, you can look at overlay.js in the "sources" tab
 * when accessing the link above.
 */

type EditImageOptions = {
  preventScaling?: boolean; // Prevents the image from being scaled
};

/** Width of the border when highlighting */
const BORDER_WIDTH: number = 1;

/** The angles the edges are from the center of an image with zero rotation. */
const CANONICAL_CORNER_ANGLES = {
  // Northeast
  0: 45,
  // Northwest
  1: 315,
  // Southwest
  2: 225,
  // Southeast
  3: 135,
} as Record<number, number>;

/** The angles the corners are from the center of an image with zero rotation. */
const CANONICAL_EDGE_ANGLES = {
  // North
  0: 0,
  // West
  1: 270,
  // South
  2: 180,
  // East
  3: 90,
} as Record<number, number>;

class EditImageOverlay extends gmAPI.OverlayView {
  /* Some properties in the constructor have a non-null assertion operator (!) because we assume they they will
     be initialized in the onAdd() method */
  bounds: google.maps.LatLngBounds;
  image: string; // A base64 representation of the image to display
  angle: number; // Image rotation in radians
  div!: HTMLDivElement;
  img!: HTMLImageElement;
  pivot: google.maps.Marker|null;
  center: google.maps.LatLng;
  rotators: google.maps.Marker[];
  scaleMode: 'none'|'shift';
  edges: Edge[];
  corners: google.maps.Marker[];
  clickArea!: google.maps.Polygon; // Handles double clicks for enabling the pivot and dragging the image
  imageRatioToWorld!: google.maps.Point;
  preventScaling: boolean;
  constructor({bounds, image = '', rotation = 0, preventScaling = false}: ImageData & EditImageOptions) {
    super();
    this.bounds = new gmAPI.LatLngBounds(
      new gmAPI.LatLng(bounds.south, bounds.west),
      new gmAPI.LatLng(bounds.north, bounds.east),
    );
    this.image = image;
    this.angle = -rotation * (Math.PI / 180); // convert image rotation degrees to angle radians
    this.pivot = null;
    this.center = this.bounds.getCenter();
    this.rotators = [];
    this.scaleMode = 'none';
    this.edges = [];
    this.corners = [];
    this.preventScaling = preventScaling;
  }

  keyDownHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') return;
    this.scaleMode = 'shift';
  };

  keyUpHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') return;
    this.scaleMode = 'none';
  };

  // #region --- OverlayView methods ---
  onAdd() {
    this.initRendering();
    this.setWorldRatio(this.getProjection());
    this.initRotators();
    this.initClickArea();

    if (this.preventScaling) return;

    document.addEventListener('keydown', this.keyDownHandler);
    document.addEventListener('keyup', this.keyUpHandler);
    this.initCornerScaling();
    this.initEdgeScaling();
  }

  onRemove() {
    document.removeEventListener('keydown', this.keyDownHandler);
    document.removeEventListener('keyup', this.keyUpHandler);
    if (this.img && this.img.parentNode) {
      this.img.parentNode.removeChild(this.img);
    }
    if (this.div?.parentNode) {
      this.div.parentNode.removeChild(this.div);
    }
    this.rotators.forEach((m) => m.setMap(null));
    this.corners.forEach((m) => m.setMap(null));
    this.edges.forEach((m) => m.setMap(null));
    this.pivot?.setMap(null);
    if (this.clickArea) {
      this.clickArea.setMap(null);
    }
  }

  draw() {
    const projection = this.getProjection();
    const centerInPixels = projection.fromLatLngToDivPixel(this.center);
    const imageSize = this.getImageSizeInPixels(true);

    this.div.style.transform = 'rotate('+this.angle+'rad)';
    if (centerInPixels) {
      this.div.style.left = centerInPixels.x - imageSize.x/2 + 'px';
      this.div.style.top = centerInPixels.y - imageSize.y/2 + 'px';
    }
    this.div.style.width = imageSize.x + 'px';
    this.div.style.height = imageSize.y + 'px';
  }

  initRendering = () => {
    this.div = document.createElement('div');
    this.div.style.borderStyle = 'solid';
    this.div.style.borderWidth = `${BORDER_WIDTH}px`;
    this.div.style.position = 'absolute';
    this.img = document.createElement('img');
    this.img.src = `data:image/png;base64,${this.image}`;
    this.img.style.position = 'absolute';
    this.img.style.width = '100%';
    this.img.style.height = '100%';
    this.img.style.top = '0';
    this.img.style.left = '0';
    this.div.appendChild(this.img);
    this.getPanes()?.markerLayer.appendChild(this.div);
  };

  setWorldRatio = (projection: google.maps.MapCanvasProjection) => {
    const sw = projection.fromLatLngToDivPixel(this.bounds.getSouthWest());
    const ne = projection.fromLatLngToDivPixel(this.bounds.getNorthEast());
    if (!sw || !ne) {
      throw new Error('Could not convert bounds to pixels');
    }
    const imageWidth = ne.x - sw.x;
    const imageHeight = sw.y - ne.y;
    const worldWidth = projection.getWorldWidth();
    // both values are divided by worldWidth because it is multiplied by that amount in getImageSizeInPixels
    // this variable is the ratio of the images width or height to the world width
    this.imageRatioToWorld = new gmAPI.Point(imageWidth/worldWidth, imageHeight/worldWidth);
  };

  /** Set up visible scaling grabbers at the corners */
  initCornerScaling = () => {
    this.getCorners().forEach((c, i) => {
      const corner = new gmAPI.Marker({
        cursor: this.center ? getScaleCursor(this.angle, CANONICAL_CORNER_ANGLES[i]) : 'pointer',
        draggable: true,
        icon: SQUARE_SVG,
        map: this.getMap(),
        position: c,
        zIndex: 3,
      });
      corner.addListener('drag', () => this.scale(corner, i));
      corner.addListener('dragend', () => {
        this.scale(corner, i);
        gmAPI.event.trigger(this, 'overlay_view_changed', {bounds: this.getBounds()});
      });
      this.corners.splice(i, 1, corner);
    });
  };

  /** Set up invisible rotating grabbers at the corners */
  initRotators = () => {
    const circleOptions = {...CIRCLE_SVG};
    if (this.preventScaling) {
      // if there is no scaling, there will be no corner indicators, so make the rotators visible
      circleOptions.fillOpacity = 90;
      circleOptions.fillColor = '#F0F0F0';
      if (circleOptions.scale) {
        circleOptions.scale *= 0.5;
      }
    }
    this.getCorners().forEach((c, i) => {
      const rotator = new gmAPI.Marker({
        cursor: getRotatorCursor(this.angle, CANONICAL_CORNER_ANGLES[i]),
        draggable: true,
        icon: circleOptions,
        map: this.getMap(),
        position: c,
        zIndex: 1,
      });
      rotator.addListener('drag', () => this.rotate(rotator, i));
      rotator.addListener('dragend', () => {
        this.rotate(rotator, i);
        gmAPI.event.trigger(this, 'overlay_view_changed', {
          bounds: this.getBounds(),
          // convert rotation from radians back to degrees
          rotation: -(this.angle * (180 / Math.PI)),
        });
      });
      this.rotators.splice(i, 1, rotator);
    });
  };

  /** Set up visible scaling grabbers on the edges */
  initEdgeScaling = () => {
    const edgeDirection = ['y', 'x', 'y', 'x'];
    const edgeSign = [1, 1, -1, -1];
    this.getEdges().forEach((c, i) => {
      const marker = new Edge({
        cursor: this.center ? getScaleCursor(this.angle, CANONICAL_EDGE_ANGLES[i]) : 'pointer',
        draggable: true,
        icon: SQUARE_SVG,
        map: this.getMap(),
        position: c,
        zIndex: 3,
        direction: edgeDirection[i],
        sign: edgeSign[i],
      });
      marker.addListener('drag', () => this.edgeScale(marker, i));
      marker.addListener('dragend', () => {
        this.edgeScale(marker, i);
        gmAPI.event.trigger(this, 'overlay_view_changed', {bounds: this.getBounds()});
      });
      this.edges.splice(i, 1, marker);
    });
  };

  initClickArea = () => {
    const map = this.getMap();
    if ((map instanceof gmAPI.Map) === false) {
      throw new Error('EditImageOverlay must be added to a google.maps.Map instance');
    }
    this.clickArea = new gmAPI.Polygon({
      draggable: true,
      fillColor: '#FF0000',
      fillOpacity: 0,
      map,
      paths: [this.getCorners()],
      strokeOpacity: 0,
      strokeWeight: 0,
    });
    this.clickArea.addListener('drag', () => this.drag());
    this.clickArea.addListener('dragend', () => {
      this.drag();
      gmAPI.event.trigger(this, 'overlay_view_changed', {bounds: this.getBounds()});
    });
    this.clickArea.addListener('dblclick', (e: google.maps.MapMouseEvent) => {
      if (!this.pivot) {
        // Create a marker for pivoting the image
        this.pivot = new gmAPI.Marker({
          position: this.center,
          draggable: true,
          icon: PIVOT_SVG,
          zIndex: 2,
        });
        this.pivot.addListener('rightclick', () => {
          this.pivot?.setMap(null);
          this.pivot = null;
        });
        this.pivot.setPosition(e.latLng);
        this.pivot.setMap(map);
      }
    });
  };

  // #endregion

  // #region --- Getters ---
  getFromContainerPixelToLatLng(point: google.maps.Point) {
    const latLng = this.getProjection().fromContainerPixelToLatLng(point);
    if (!latLng) {
      throw new Error('fromContainerPixelToLatLng: latLng is null');
    }
    return latLng;
  }

  getFromLatLngToContainerPixel(latLng: google.maps.LatLng | google.maps.LatLngLiteral) {
    const point = this.getProjection().fromLatLngToContainerPixel(latLng);
    if (!point) {
      throw new Error('fromLatLngToContainerPixel: point is null');
    }
    return point;
  }

  getMarkerPosition(marker: google.maps.Marker) {
    const position = marker.getPosition();
    if (!position) {
      throw new Error('getMarkerPosition: position is null');
    }
    return position;
  }
  // #endregion

  // #region --- Calculations ---
  getImageSizeInPixels(includeBorder: boolean = false) {
    const worldWidth = this.getProjection().getWorldWidth();
    let x = this.imageRatioToWorld.x * worldWidth;
    let y = this.imageRatioToWorld.y * worldWidth;
    if (includeBorder) {
      x += 2 * BORDER_WIDTH;
      y += 2 * BORDER_WIDTH;
    }
    return new gmAPI.Point(x, y);
  }

  getCorners(): google.maps.LatLng[] {
    const center = this.center;
    const centerInPixels = this.getFromLatLngToContainerPixel(center);
    const imageSize = this.getImageSizeInPixels();

    return [
      this.getFromContainerPixelToLatLng(computeOffset(
        centerInPixels,
        imageSize.x/2,
        -imageSize.y/2,
        this.angle,
      )), // sw
      this.getFromContainerPixelToLatLng(computeOffset(
        centerInPixels,
        -imageSize.x/2,
        -imageSize.y/2,
        this.angle,
      )), // nw
      this.getFromContainerPixelToLatLng(computeOffset(
        centerInPixels,
        -imageSize.x/2,
        imageSize.y/2,
        this.angle,
      )), // ne
      this.getFromContainerPixelToLatLng(computeOffset(
        centerInPixels,
        imageSize.x/2,
        imageSize.y/2,
        this.angle,
      )), // se
    ];
  }

  getCornersInPixels() {
    const center = this.center;
    const centerInPixels = this.getFromLatLngToContainerPixel(center);
    const imageSize = this.getImageSizeInPixels();

    return [
      computeOffset(centerInPixels, imageSize.x/2, -imageSize.y/2, this.angle), // sw
      computeOffset(centerInPixels, -imageSize.x/2, -imageSize.y/2, this.angle), // nw
      computeOffset(centerInPixels, -imageSize.x/2, imageSize.y/2, this.angle), // ne
      computeOffset(centerInPixels, imageSize.x/2, imageSize.y/2, this.angle), // se
    ];
  }

  // Get the locations for each of the edge placemarkers
  getEdges() {
    return getEdges(this.getCorners());
  }

  getBounds() {
    const center = this.center;
    const centerInPixels = this.getFromLatLngToContainerPixel(center);
    const imageSize = this.getImageSizeInPixels();

    const sw = this.getFromContainerPixelToLatLng(
      new gmAPI.Point(centerInPixels.x - imageSize.x/2, centerInPixels.y + imageSize.y/2));
    const ne = this.getFromContainerPixelToLatLng(
      new gmAPI.Point(centerInPixels.x + imageSize.x/2, centerInPixels.y - imageSize.y/2));
    return new gmAPI.LatLngBounds(sw, ne);
  }
  // #endregion

  // #region --- Functional calculations for scaling, rotating, and dragging ---
  rotate(corner: google.maps.Marker, cornerIndex: number) {
    const pivot = this.pivot ? this.getMarkerPosition(this.pivot) : this.center;
    const position = this.getMarkerPosition(corner);
    const eventMarker = this.getFromLatLngToContainerPixel(position);
    const screenCenter = this.getFromLatLngToContainerPixel(this.center);
    const screenPivot = this.getFromLatLngToContainerPixel(pivot);

    // Update angle using pivot points
    const oldMarker = this.getCornersInPixels()[cornerIndex];
    const oldAngle = Math.atan2(-(oldMarker.y - screenPivot.y), oldMarker.x - screenPivot.x);
    const newAngle = Math.atan2(-(eventMarker.y - screenPivot.y), eventMarker.x - screenPivot.x);
    const deltaAngle = oldAngle - newAngle;
    this.angle += deltaAngle;
    if (this.angle > Math.PI) {
      this.angle -= 2 * Math.PI;
    }
    if (this.angle < -Math.PI) {
      this.angle += 2 * Math.PI;
    }

    // recalculate the center of the image
    const newScreenCenter = new gmAPI.Point(
      screenPivot.x + (screenCenter.x - screenPivot.x) *
      Math.cos(deltaAngle) - (screenCenter.y - screenPivot.y) *
      Math.sin(deltaAngle),
      screenPivot.y + (screenCenter.x - screenPivot.x) *
      Math.sin(deltaAngle) + (screenCenter.y - screenPivot.y) *
      Math.cos(deltaAngle));
    this.center = this.getFromContainerPixelToLatLng(newScreenCenter);

    this.updatePlacemarkers();
  }

  scale(corner: google.maps.Marker, cornerIndex: number) {
    const corners = this.getCorners();
    const screenOldCorner = this.getFromLatLngToContainerPixel(corners[cornerIndex]);
    const screenEventMarker = this.getFromLatLngToContainerPixel(this.getMarkerPosition(corner));
    // either pivot from the pivot point or the opposite corner
    const pivot = (this.pivot ? this.pivot.getPosition() : corners[(cornerIndex+2)%4]);
    if (!pivot) {
      throw new Error('cannot scale: pivot is null');
    }
    const screenPivot = this.getFromLatLngToContainerPixel(pivot);
    const pivotToEvent = new Vector(screenEventMarker.x - screenPivot.x, screenEventMarker.y - screenPivot.y);
    const pivotToOldCorner = new Vector(screenOldCorner.x - screenPivot.x, screenOldCorner.y - screenPivot.y);
    if (this.scaleMode === 'shift') {
      const scaleFactor = pivotToEvent.length() / pivotToOldCorner.length();
      this.scaleFromFactors(scaleFactor, scaleFactor, pivot);
    } else {
      const scaleFactorX = pivotToEvent.projectOnAngle(this.angle).length() /
        pivotToOldCorner.projectOnAngle(this.angle).length();
      const scaleFactorY = pivotToEvent.projectOnAngle(this.angle - Math.PI/2).length() /
        pivotToOldCorner.projectOnAngle(this.angle - Math.PI/2).length();

      this.scaleFromFactors(scaleFactorX, scaleFactorY, pivot);
    }
    this.updatePlacemarkers();
  }

  scaleFromFactors(scaleFactorX: number, scaleFactorY: number, pivot: google.maps.LatLng) {
    const screenCenter = this.getFromLatLngToContainerPixel(this.center);
    const screenPivot = this.getFromLatLngToContainerPixel(pivot);
    const pivotToCenter = new Vector(screenCenter.x - screenPivot.x, screenCenter.y - screenPivot.y);

    this.imageRatioToWorld.x = this.imageRatioToWorld.x * scaleFactorX;
    this.imageRatioToWorld.y = this.imageRatioToWorld.y * scaleFactorY;

    const pivotToCenterOnWidth = pivotToCenter.projectOnAngle(this.angle).length() *
      scaleFactorX *
      Math.sign(pivotToCenter.projectOnAngle(this.angle).components[0]);
    const pivotToCenterOnHeight = pivotToCenter.projectOnAngle(this.angle + Math.PI/2).length() *
      scaleFactorY *
      Math.sign(pivotToCenter.projectOnAngle(this.angle+ Math.PI/2).components[1]);
    const newScreenCenter = new gmAPI.Point(
      screenPivot.x + Math.sign(Math.cos(this.angle)) *
      (pivotToCenterOnWidth * Math.cos(this.angle) - pivotToCenterOnHeight *
      Math.sin(this.angle)),
      screenPivot.y + Math.sign(Math.cos(this.angle)) *
      (pivotToCenterOnWidth * Math.sin(this.angle) + pivotToCenterOnHeight *
      Math.cos(this.angle)),
    );
    this.center = this.getFromContainerPixelToLatLng(newScreenCenter);
  }

  /**
   * Scale the image overlay around the pivot point.
   * @param edge
   * @param edgeIndex
   */
  edgeScale(edge: Edge, edgeIndex: number) {
    const edges = this.getEdges();
    const screenOldEdge = this.getFromLatLngToContainerPixel(edges[edgeIndex]);
    const edgePosition = edge.getPosition();
    if (!edgePosition) {
      throw new Error('edge position is null');
    }
    const screenEventMarker = this.getFromLatLngToContainerPixel(edgePosition);
    const screenCenter = this.getFromLatLngToContainerPixel(this.center);

    if (this.pivot) {
      const pivotPosition = this.getMarkerPosition(this.pivot);
      const screenPivot = this.getFromLatLngToContainerPixel(pivotPosition);
      const pivotToOld = new Vector(screenOldEdge.x - screenPivot.x, screenOldEdge.y - screenPivot.y);
      const pivotToEvent = new Vector(screenEventMarker.x - screenPivot.x, screenEventMarker.y - screenPivot.y);
      if (edge.direction == 'x') {
        this.scaleFromFactors(
          pivotToEvent.length() / pivotToOld.length(),
          1,
          this.getMarkerPosition(this.pivot),
        );
      } else {
        this.scaleFromFactors(
          1,
          pivotToEvent.length() / pivotToOld.length(),
          this.getMarkerPosition(this.pivot),
        );
      }
    } else {
      const screenOppositeEdge = this.getFromLatLngToContainerPixel(edges[(edgeIndex+2)%4]);
      const oldDistance = Math.hypot(
        screenOldEdge.x - screenOppositeEdge.x,
        screenOldEdge.y - screenOppositeEdge.y,
      );
      const newDistance = Math.hypot(
        screenEventMarker.x - screenOppositeEdge.x,
        screenEventMarker.y - screenOppositeEdge.y,
      );
      this.imageRatioToWorld[edge.direction as 'x' | 'y'] = this.imageRatioToWorld[edge.direction as 'x' | 'y'] * newDistance / oldDistance;
      if (edge.direction == 'x') {
        const newScreenCenter = new gmAPI.Point(
          screenCenter.x + edge.sign * Math.cos(this.angle)*(oldDistance - newDistance)/2,
          screenCenter.y + edge.sign * Math.sin(this.angle)*(oldDistance - newDistance)/2);
        this.center = this.getFromContainerPixelToLatLng(newScreenCenter);
      } else {
        const newScreenCenter = new gmAPI.Point(
          screenCenter.x - edge.sign * Math.sin(this.angle)*(oldDistance - newDistance)/2,
          screenCenter.y + edge.sign * Math.cos(this.angle)*(oldDistance - newDistance)/2);
        this.center = this.getFromContainerPixelToLatLng(newScreenCenter);
      }
    }
    this.updatePlacemarkers();
  }

  drag() {
    const newPath = this.clickArea.getPath().getArray();
    const oldCorners = this.getCorners();
    this.center = new gmAPI.LatLng(
      this.center.lat() + newPath[0].lat() - oldCorners[0].lat(),
      this.center.lng() + newPath[0].lng() - oldCorners[0].lng(),
    );
    if (this.pivot) {
      this.pivot.setPosition(new gmAPI.LatLng(
        (this.getMarkerPosition(this.pivot)).lat() + newPath[0].lat() - oldCorners[0].lat(),
        (this.getMarkerPosition(this.pivot)).lng() + newPath[0].lng() - oldCorners[0].lng(),
      ));
    }
    this.updatePlacemarkers();
  }

  updatePlacemarkers() {
    // Update the location of the edge placemarkers
    const edges = this.getEdges();
    this.edges.forEach((m, i) => {
      m.setPosition(edges[i]);
      m.setCursor(getScaleCursor(this.angle, CANONICAL_EDGE_ANGLES[i]));
    });
    // Update the location of the corner placemarkers
    const corners = this.getCorners();
    this.rotators.forEach((m, i) => {
      m.setPosition(corners[i]);
      m.setCursor(getRotatorCursor(this.angle, CANONICAL_CORNER_ANGLES[i]));
    });
    this.clickArea.setPath(corners);
    this.corners.forEach((m, i) => {
      m.setPosition(corners[i]);
      m.setCursor(getScaleCursor(this.angle, CANONICAL_CORNER_ANGLES[i]));
    });
  }
  // #endregion
}

export {
  EditImageOverlay,

  // exported for tests
  computeOffset,
};
